import { Fragment, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, type Summary as SummaryData } from "../api";
import { useAuth } from "../auth";
import { EYE_LABEL, useLoad } from "../hooks";

export function ResultsPage() {
  const id = Number(useParams().id);
  const { user } = useAuth();
  const [tab, setTab] = useState<"summary" | "debug">("summary");
  const staff = user?.role !== "patient";
  const summary = useLoad(() => api.summary(id), [id]);
  // staff see everything; a patient what their debug mode shares (the comprehensive report)
  const access = summary.data?.debug_access ?? { on_screen: staff, pdf: staff };
  const tabs = staff || access.on_screen;
  return (
    <>
      {tabs && (
        <div className="tabs">
          <button className={tab === "summary" ? "active" : ""} onClick={() => setTab("summary")}>{staff ? "Summary" : "Results"}</button>
          <button className={tab === "debug" ? "active" : ""} onClick={() => setTab("debug")}>{staff ? "Debug" : "Full report"}</button>
        </div>
      )}
      {tab === "summary" || !tabs ? <Summary id={id} staff={staff} summary={summary} pdf={access.pdf} />
        : <Debug id={id} staff={staff} pdf={access.pdf} />}
    </>
  );
}

/** Names are worth showing when a session ran more than one test of a kind per eye. */
function named(items: any[] | undefined): boolean {
  if (!items) return false;
  const kinds = items.map((x) => x.kind);
  return new Set(kinds).size !== kinds.length;
}

/** A patient with more programs to do in this sitting: the next one, without checking
 *  the screen or measuring brightness again. */
function NextInVisit({ visitId, patientId }: { visitId: string; patientId: number }) {
  const plan = useLoad(() => api.myPlan(visitId), [visitId]);
  const nav = useNavigate();
  const p = plan.data;
  if (!p) return null;
  const nextId = p.run[0];
  const name = (id: number) => p.items.find((x) => x.program_id === id)?.name ?? "";
  if (nextId !== undefined) {
    return (
      <div className="card ok-card">
        <h2 style={{ marginTop: 0 }}>Next: {name(nextId)}</h2>
        <p>{p.run.length === 1 ? "One more test today." : `${p.run.length} more tests today.`}</p>
        <button className="big" onClick={() => nav(`/patients/${patientId}/new-session?visit=${encodeURIComponent(visitId)}&program=${nextId}`)}>
          Start {name(nextId)}</button>
      </div>
    );
  }
  if (p.mode === "patient_picks" && p.choose.length) {
    return (
      <div className="card">
        <button className="secondary" onClick={() => nav(`/patients/${patientId}/new-session?visit=${encodeURIComponent(visitId)}`)}>
          Do another test</button>
      </div>
    );
  }
  return null;
}

function Summary({ id, staff, summary, pdf }: {
  id: number; staff: boolean; summary: { data: SummaryData | null; error: string | null }; pdf: boolean;
}) {
  const session = useLoad(() => api.session(id), [id]);
  const s = summary.data;
  if (summary.error) return <div className="error">{summary.error}</div>;
  if (!s) return <div>Loading…</div>;
  const flags = session.data?.change_summary?.flags ?? [];
  const screen = session.data?.change_summary?.screen;
  const unapproved = session.data?.qualification?.monitor_class === "unapproved";
  return (
    <>
      <h1>Results — {new Date(s.date).toLocaleDateString()}{s.demo && <> <span className="badge demo">debug</span></>}</h1>
      {s.demo_note && <div className="card demo-card"><p style={{ margin: 0 }}>{s.demo_note}</p></div>}
      {s.contact_message && (
        <div className="card alert-card" role="alert">
          <h2 style={{ marginTop: 0 }}>Your result changed</h2>
          <p style={{ fontSize: "1.2rem" }}>{s.contact_message}</p>
          <p className="small">{s.alerts.map((a) => `${a.eye_label} ${a.kind === "acuity" ? "acuity" : "contrast sensitivity"}`).join("; ")}</p>
        </div>
      )}
      {s.results.map((r) => (
        <div className="card" key={r.eye}>
          <h2 style={{ marginTop: 0 }}>{r.eye_label}</h2>
          {(r.items ?? [r.acuity, r.contrast].filter(Boolean)).map((it: any, i: number) => (
            <p key={i} className={it.invalid ? "warn" : ""}>
              {named(r.items) && it.name && <><strong>{it.name}{it.times > 1 ? ` (${it.repeat + 1} of ${it.times})` : ""}{it.purpose !== "main" ? ", repeated" : ""}:</strong> </>}
              {it.text}{it.comparison && <><br /><span className="muted">{it.comparison}</span></>}
            </p>
          ))}
        </div>
      ))}
      {!staff && session.data?.visit_id && <NextInVisit visitId={session.data.visit_id} patientId={session.data.patient_id} />}
      <p className="muted small">{s.note}</p>
      {staff && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>For the research team</h2>
          {flags.length > 0 ? (
            <ul>
              {flags.map((f, i) => <li key={i} className={String(f.status).endsWith("worse") ? "bad" : ""}>{String(f.eye)} {String(f.kind)}: {String(f.status).replace("_", " ")}</li>)}
            </ul>
          ) : <p className="muted">No change flags.</p>}
          {screen && (
            <p className={screen.status === "drifted" ? "bad" : screen.status === "steady" ? "" : "warn"}>
              Screen brightness: {screen.status === "drifted" ? <>drifted during the session
                ({[screen.drift_from_start_pct != null && `${screen.drift_from_start_pct > 0 ? "+" : ""}${screen.drift_from_start_pct}% from the start`,
                  screen.drift_from_baseline_pct != null && `${screen.drift_from_baseline_pct > 0 ? "+" : ""}${screen.drift_from_baseline_pct}% from the baseline`]
                  .filter(Boolean).join(", ")})</> : screen.status === "steady" ? "steady before and after" : "not measured after the last test"}
            </p>
          )}
          {unapproved && <p className="warn">Unapproved monitor: size from its EDID, nominal sRGB response.</p>}
          <p><a href={api.reportUrl(id)} target="_blank" rel="noreferrer">Open the full PDF report</a></p>
          {session.data && <Link to={`/patients/${session.data.patient_id}`}>Back to patient</Link>}
        </div>
      )}
      {!staff && pdf && (
        <p><a href={api.reportUrl(id, true)} target="_blank" rel="noreferrer">Full report (PDF)</a></p>
      )}
      <button className="secondary" onClick={() => window.print()}>Print</button>
    </>
  );
}

const SNAP_ROWS: Array<[string, string]> = [
  ["screen_label", "screen name"], ["width_px", "width (device px)"], ["height_px", "height (device px)"],
  ["device_pixel_ratio", "device pixel ratio"], ["canvas_width_px", "canvas width px"], ["canvas_height_px", "canvas height px"],
  ["registered_width_px", "registered width px"], ["physical_width_mm", "width (mm)"], ["physical_height_mm", "height (mm)"],
  ["size_source", "size from"], ["diagonal_in", "diagonal (in)"], ["ppi", "pixels per inch"], ["aspect_ratio", "aspect ratio"],
  ["viewing_distance_mm", "viewing distance (mm)"], ["px_per_deg", "pixels per degree"], ["arcmin_per_px", "arcmin per pixel"],
  ["color_depth", "Chrome colorDepth (30 = HDR screen; not bit depth)"], ["dynamic_range_high", "HDR reported"], ["color_gamut", "gamut"],
  ["buffer_format", "drawing buffer"], ["output_bits", "output bits"], ["gpu", "GPU"], ["refresh_hz", "refresh (Hz)"],
  ["calibration_tier", "calibration"], ["white_cd_m2", "white (cd/m²)"], ["os_display_mode", "OS display mode"],
  ["background_code10", "background (/1023)"], ["user_agent", "browser"],
];

const TRIAL_COLS: Array<[string, string]> = [
  ["seq", "#"], ["phase", "phase"], ["staircase", "stair"], ["requested_value", "requested"], ["presented_value", "presented"],
  ["logmar", "logMAR"], ["stroke_px", "stroke px"], ["letter_px", "letter px"], ["weber", "Weber"],
  ["bg_code10", "bg /1023"], ["letter_code10", "E /1023"], ["orientation", "E dir"], ["response", "answer"],
  ["correct", "ok"], ["rt_ms", "RT ms"],
];

function fmt(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : Math.abs(v) < 10 ? v.toFixed(4) : v.toFixed(1);
  return String(v);
}

function KV({ obj, rows }: { obj: Record<string, any> | null | undefined; rows: Array<[string, string]> }) {
  if (!obj) return <p className="muted small">not recorded</p>;
  return (
    <dl className="kv">
      {rows.map(([k, label]) => <Fragment key={k}><dt>{label}</dt><dd>{fmt(obj[k])}</dd></Fragment>)}
    </dl>
  );
}

/** Interleaved contrast staircases: each one's start, trials, reversals and threshold. */
function StaircaseTable({ r }: { r: Record<string, any> }) {
  const per: Record<string, any> = Object.fromEntries((r.per_staircase ?? []).map((x: any) => [x.label, x]));
  const starts: number[] = r.rule?.starts_log_cs ?? [];
  const staggered = r.rule?.stagger && r.rule.stagger !== "none";
  const c = r.catch ?? {};
  return (
    <div className="small">
      <p style={{ marginBottom: "0.2rem" }}>
        {r.n_staircases} interleaved {r.staircase_type} staircases: log CS {fmt(r.log_cs)} = mean of their thresholds;
        SD between them {fmt(r.spread_log_cs)}{r.spread_flag ? " (they disagree: worth repeating)" : ""}.
        {r.rule?.pick === "balanced" ? " Balanced picking." : ""}{r.rule?.stop_rule === "settled" ? ` Each stopped when settled (${r.rule.stop_reversals ?? "SD"}${r.rule.stop_reversals ? " reversals" : ""}).` : ""}
        {c.total ? ` Attention checks: ${c.correct} of ${c.total} right.` : ""}
        {r.order_verified ? " Trial order replayed from its seed." : ""}
      </p>
      <table className="dense">
        <thead><tr><th>staircase</th><th>start log CS</th>{staggered && <th>joined after</th>}<th>trials</th><th>reversals</th><th>threshold log CS</th></tr></thead>
        <tbody>{Object.entries(r.staircases_log_cs as Record<string, number | null>).map(([label, v], i) => (
          <tr key={label}><td>{label}</td><td>{fmt(starts[i])}</td>{staggered && <td>{fmt(per[label]?.joined_after)} trials</td>}
            <td>{fmt(per[label]?.trials)}</td><td>{fmt(per[label]?.reversals)}</td><td>{fmt(v)}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Debug({ id, staff, pdf }: { id: number; staff: boolean; pdf: boolean }) {
  const d = useLoad(() => api.sessionDebug(id), [id]);
  if (d.error) return <div className="error">{d.error}</div>;
  if (!d.data) return <div>Loading…</div>;
  const { session, display_profile, calibration, tests, program, brightness, monitor_report } = d.data;
  const q = session.qualification;
  return (
    <>
      <h1>{staff ? "Debug" : "Full report"} — session {session.id} ({session.study_id})</h1>
      <p>
        {pdf && <><a href={api.reportUrl(id, true)} target="_blank" rel="noreferrer">{staff ? "Debug PDF report" : "Full report (PDF)"}</a>{" · "}</>}
        <a href={api.trialsCsvUrl(id)}>All trials as CSV</a>
      </p>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Screen check {q ? (q.qualified ? <span className="good">passed</span> : <span className="bad">failed</span>) : <span className="muted">not run</span>}
          {q?.demo && <> <span className="badge demo">debug: checks recorded, not enforced (by {q.demo.by ?? "an admin"})</span></>}</h2>
        {q && (
          <table>
            <tbody>
              {q.checks.map((c: any) => (
                <tr key={c.key}><td>{c.label}</td><td className={c.ok ? "good" : c.critical ? "bad" : "warn"}>{c.ok ? "ok" : c.critical ? "fail" : c.skipped_for_demo ? "skipped (debug)" : "note"}</td><td className="small">{c.detail}</td></tr>
              ))}
            </tbody>
          </table>
        )}
        {q && <p className="small muted">Model: {q.model_name ?? "none"} · platform {q.platform} · HDR link (PQ) {fmt(q.pq_link)} · contrast ceiling about log CS {q.ceiling_log_cs}</p>}
        <p className="small muted">Program: {program ? `${program.name} (version ${program.version ?? "?"})` : session.protocol?.name ?? "—"}</p>
      </div>
      {brightness && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Brightness (lux meter) {brightness.ok ? <span className="good">in range</span> : <span className="bad">not in range</span>}</h2>
          {brightness.spec ? (
            <p className="small">Older photometer check: target {brightness.spec.target} {brightness.spec.unit}, last reading {fmt(brightness.readings?.at(-1)?.value)}</p>
          ) : (
            <table className="dense"><thead><tr><th>when</th><th>lux</th><th>verdict</th><th>vs baseline</th></tr></thead>
              <tbody>
                <tr><td>{brightness.phase === "baseline" ? "before (saved as baseline)" : "before"}</td>
                  <td>{fmt(brightness.mean_lux)}</td><td>{brightness.verdict}</td>
                  <td>{brightness.baseline_lux != null ? `${brightness.baseline_lux} lux${brightness.drift_pct != null ? `, ${brightness.drift_pct}%` : ""}` : "—"}</td></tr>
                {d.data.brightness_end && (
                  <tr><td>after (older sessions)</td><td>{fmt(d.data.brightness_end.mean_lux)}</td>
                    <td>{d.data.brightness_end.verdict}</td>
                    <td>{fmt(d.data.brightness_end.drift_from_baseline_pct)}% (from start {fmt(d.data.brightness_end.drift_from_start_pct)}%)</td></tr>
                )}
              </tbody></table>
          )}
        </div>
      )}
      {monitor_report && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Monitor identity (helper report {monitor_report.id}, {new Date(monitor_report.created_at).toLocaleString()})</h2>
          <p className="small muted">{monitor_report.platform} · {monitor_report.os_version}</p>
          <table className="dense">
            <thead><tr><th>maker</th><th>name</th><th>product</th><th>serial</th><th>year</th><th>bits</th><th>HDR (PQ)</th><th>size mm</th><th>native px</th><th>from</th></tr></thead>
            <tbody>{monitor_report.monitors.map((m: any, i: number) => (
              <tr key={i}><td>{m.manufacturer}</td><td>{m.capabilities?.name}</td><td>{fmt(m.product_code)}</td><td>{m.serial_text || fmt(m.serial_number)}</td>
                <td>{fmt(m.year)}</td><td>{fmt(m.capabilities?.panel_bits)}</td><td>{fmt(m.capabilities?.hdr_pq)}</td>
                <td>{m.capabilities?.physical_size_mm?.join("×")}</td><td>{m.capabilities?.native_px?.join("×")}</td><td>{m.source}</td></tr>
            ))}</tbody>
          </table>
          {monitor_report.extra && <details><summary className="small">System report from the helper</summary><pre className="small" style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(monitor_report.extra, null, 2)}</pre></details>}
        </div>
      )}
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Display profile and calibration</h2>
        <KV obj={display_profile} rows={[["label", "label"], ["browser_label", "screen name"], ["width_px", "width px"], ["height_px", "height px"], ["width_mm", "width mm"], ["height_mm", "height mm"], ["size_source", "size from"], ["model_id", "model"]]} />
        <KV obj={calibration} rows={[["tier", "tier"], ["white_cd_m2", "white cd/m²"], ["black_cd_m2", "black cd/m²"], ["output_bits", "output bits"], ["os_mode", "OS mode"], ["instrument", "instrument"]]} />
        {calibration?.step_test && <p className="small">step test: {JSON.stringify(calibration.step_test)}</p>}
      </div>
      {tests.map((t: any) => (
        <div className="card" key={t.id}>
          <h2 style={{ marginTop: 0 }}>{EYE_LABEL[t.eye] ?? t.eye} — {t.experiment?.name || t.kind}{t.experiment && t.experiment.times > 1 ? ` (${t.experiment.repeat + 1} of ${t.experiment.times})` : ""} <span className="muted small">test {t.id}{t.retry_of ? `, ${t.purpose === "confirm" ? "confirmation of" : "repeat of"} ${t.retry_of}` : ""}, {t.status}</span></h2>
          {t.result && (
            <p className="small">
              valid: <strong className={t.result.valid === false ? "bad" : "good"}>{fmt(t.result.valid)}</strong>
              {t.result.invalid_reasons?.length ? ` — ${t.result.invalid_reasons.join("; ")}` : ""}
              {t.result.warnings?.length ? <span className="warn"> · {t.result.warnings.join("; ")}</span> : null}
              {t.result.frames && <> · frames {t.result.frames.frames}, late {t.result.frames.dropped}, worst {t.result.frames.worst_frame_ms} ms</>}
              {t.result.alert && t.result.alert !== "none" && <span className="warn"> · change alert {t.result.alert} ({fmt(t.result.change_delta)} vs baseline)</span>}
            </p>
          )}
          {t.result?.staircases_log_cs && <StaircaseTable r={t.result} />}
          <details><summary>Display at test start</summary><KV obj={t.display_snapshot} rows={SNAP_ROWS} /></details>
          {t.compliance?.events?.length > 0 && (
            <details open><summary>Compliance events</summary>
              <ul className="small">{t.compliance.events.map((e: any, i: number) => <li key={i} className={e.severity === "critical" ? "bad" : "warn"}>{e.t_ms} ms: {e.type} {e.detail ?? ""}</li>)}</ul>
            </details>
          )}
          <details><summary>Trials ({t.trials.length})</summary>
            <div className="scroll">
              <table className="dense">
                <thead><tr>{TRIAL_COLS.map(([, h]) => <th key={h}>{h}</th>)}</tr></thead>
                <tbody>{t.trials.map((tr: any) => <tr key={tr.seq}>{TRIAL_COLS.map(([k]) => <td key={k}>{fmt(tr[k])}</td>)}</tr>)}</tbody>
              </table>
            </div>
          </details>
        </div>
      ))}
    </>
  );
}
